<!--
  管理后台顶部导航栏组件 (AdminHeader.vue)
  
  功能说明：
  - 显示当前页面标题
  - 提供侧边栏切换按钮（菜单图标）
  - 显示告警通知按钮（有未读告警时显示徽章，无未读告警时也显示按钮）
  - 点击通知按钮跳转到告警列表页面
  - 显示管理员信息（头像、名称）
  - 提供退出登录功能
  
  组件特性：
  - 响应式设计，适配移动端和桌面端
  - 使用玻璃态效果（glass）增强视觉效果
  - 集成 Varlet UI 组件库
-->
<template>
  <!-- 
    顶部导航栏容器
    - glass: 玻璃态效果类名（毛玻璃背景）
    - 固定高度 64px，水平布局
  -->
  <div class="admin-header glass">
    <!-- 左侧区域：菜单按钮和页面标题 -->
    <div class="header-left">
      <!-- 
        菜单图标按钮
        - name="menu": 使用菜单图标
        - @click: 点击时触发切换侧边栏事件
        - 用于展开/收起侧边栏
      -->
      <var-icon name="menu" class="menu-icon" @click="toggleSidebar" />
      
      <!-- 
        页面标题
        - 动态显示当前页面标题（由父组件传入）
        - 根据路由变化自动更新
      -->
      <h1 class="header-title">{{ title }}</h1>
    </div>
    
    <!-- 右侧区域：通知图标和管理员信息 -->
    <div class="header-right">
      <!-- 
        右侧操作区域容器
        - var-space: Varlet 间距组件，设置水平间距 16px
        - 包含通知按钮和管理员下拉菜单
      -->
      <var-space :size="[16, 0]">
        <!-- 
          通知按钮（始终显示，方便用户随时查看告警）
          - 通知按钮应该始终可见，让用户可以随时点击查看告警列表
          - 徽章仅在未读告警数大于 0 时显示
        -->
        <var-badge v-if="unreadAlerts > 0 && adminStore.hasPermission('announcements')" :value="unreadAlerts" :max="99">
          <!-- 
            通知按钮
            - text: 文本按钮样式（无背景）
            - round: 圆角按钮
            - @click: 点击时触发显示告警列表事件，跳转到告警列表页面
          -->
          <var-button
            text
            round
            color="var(--text-primary)"
            @click.stop="handleShowAlertsClick"
          >
            <var-icon name="notifications" size="20" />
          </var-button>
        </var-badge>
        
        <!-- 
          通知按钮（无未读告警时显示，不显示徽章）
          - 当没有未读告警时，仍然显示通知按钮，但不显示徽章
          - 这样用户可以随时点击查看所有告警（包括已读的）
        -->
        <var-button
          v-if="unreadAlerts === 0 && adminStore.hasPermission('announcements')"
          text
          round
          color="var(--text-primary)"
          @click.stop="handleShowAlertsClick"
        >
          <var-icon name="notifications" size="20" />
        </var-button>
        
        <!-- 
          管理员信息：点击打开菜单（使用自定义弹出层，兼容 PC/手机）
        -->
        <div
          ref="adminTriggerRef"
          class="admin-trigger"
          @click.stop="showAdminMenu = !showAdminMenu"
        >
          <var-avatar :src="adminStore.adminInfo?.avatar" size="small">
            <var-icon name="account-circle" />
          </var-avatar>
          <span class="admin-name">{{ adminStore.adminInfo?.name || '管理员' }}</span>
          <var-icon name="chevron-down" size="16" :class="{ 'arrow-open': showAdminMenu }" />
        </div>

        <!-- 弹出菜单：遮罩 + 菜单列表，点击遮罩关闭 -->
        <Teleport to="body">
          <div v-show="showAdminMenu" class="admin-menu-overlay" @click="showAdminMenu = false">
            <div
              ref="adminMenuRef"
              class="admin-menu-panel"
              :style="menuPositionStyle"
              @click.stop
            >
              <div class="admin-menu-item" @click="onMenuLogout">
                <var-icon name="logout" size="20" />
                <span>退出登录</span>
              </div>
            </div>
          </div>
        </Teleport>
      </var-space>
    </div>
  </div>
</template>

<script setup>
/**
 * Vue Composition API 导入
 */
import { ref, watch, nextTick } from 'vue'

/**
 * Vue Router 导入
 * - useRouter: 获取路由实例，用于程序式导航
 */
import { useRouter } from 'vue-router'

/**
 * Pinia Store 导入
 * - useAdminStore: 获取管理员状态管理 store
 * - 用于访问管理员信息和执行登录/登出操作
 */
import { useAdminStore } from '@/stores/AdminStore'

/**
 * API 工具导入
 * - adminApi: 管理员相关的 API 请求工具
 * - 封装了 HTTP 请求方法，包含认证信息
 */
import adminApi from '@/utils/adminApi'

/**
 * Varlet UI 组件导入
 * - Snackbar: 消息提示组件，用于显示成功/错误提示
 * - Dialog: 对话框组件，用于显示确认对话框
 */
import { Snackbar, Dialog } from '@varlet/ui'

/**
 * 组件 Props 定义
 * 接收父组件传递的属性
 */
defineProps({
  /**
   * 页面标题
   * - type: String - 字符串类型
   * - default: '管理后台' - 默认值
   * - 用于显示当前页面的标题
   */
  title: {
    type: String,
    default: '管理后台'
  },
  /**
   * 未读告警数量
   * - type: Number - 数字类型
   * - default: 0 - 默认值为 0
   * - 用于在通知图标上显示未读告警的徽章数量
   */
  unreadAlerts: {
    type: Number,
    default: 0
  }
})

/**
 * 组件事件定义
 * 向父组件发送的事件
 * - 'toggle-sidebar': 切换侧边栏展开/收起状态
 * - 'show-alerts': 显示告警列表
 */
const emit = defineEmits(['toggle-sidebar', 'show-alerts'])

/**
 * 响应式数据定义
 */
// 路由实例，用于程序式导航
const router = useRouter()

// 管理员状态管理 store，用于访问管理员信息和执行状态操作
const adminStore = useAdminStore()

// 管理员菜单：是否显示、触发器与菜单定位
const showAdminMenu = ref(false)
const adminTriggerRef = ref(null)
const adminMenuRef = ref(null)
const menuPositionStyle = ref({})

watch(showAdminMenu, (open) => {
  if (open) {
    nextTick(() => updateMenuPosition())
  }
})

function updateMenuPosition() {
  const el = adminTriggerRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  menuPositionStyle.value = {
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
    minWidth: `${Math.max(rect.width, 140)}px`
  }
}

const onMenuLogout = () => {
  showAdminMenu.value = false
  handleLogout()
}

/**
 * 方法定义
 */
/**
 * 切换侧边栏
 * 点击菜单图标时触发，向父组件发送切换侧边栏事件
 * 父组件（AdminLayout）会监听此事件并切换侧边栏状态
 */
const toggleSidebar = () => {
  emit('toggle-sidebar')
}

/**
 * 处理通知按钮点击事件
 * 点击通知图标时触发，向父组件发送显示告警列表事件
 * 使用 .stop 修饰符阻止事件冒泡，确保事件正确触发
 */
const handleShowAlertsClick = () => {
  console.log('通知按钮被点击，触发 show-alerts 事件')
  emit('show-alerts')
}

/**
 * 处理退出登录
 * 
 * 流程说明：
 * 1. 显示确认对话框，询问用户是否确定退出
 * 2. 用户确认后，调用后端 API 执行登出操作
 * 3. 无论 API 请求成功或失败，都会清除本地状态
 * 4. 显示成功提示
 * 5. 跳转到登录页面
 * 
 * 错误处理：
 * - 即使后端登出请求失败，也会清除本地状态
 * - 这样可以确保用户能够正常退出，避免因网络问题导致无法退出
 */
const handleLogout = () => {
  // 显示确认对话框
  Dialog({
    title: '确认退出',              // 对话框标题
    message: '确定要退出登录吗？',   // 对话框内容
    confirmButtonText: '退出',      // 确认按钮文字
    cancelButtonText: '取消',       // 取消按钮文字
    /**
     * 确认回调函数
     * 用户点击"退出"按钮时执行
     */
    onConfirm: async () => {
      try {
        // 调用后端 API 执行登出操作
        // 通知服务器清除服务端的会话信息
        await adminApi.post('/logout')
      } catch (error) {
        // 即使请求失败也清除本地状态
        // 这样可以避免因网络问题导致用户无法退出
        console.error('登出请求失败:', error)
      } finally {
        // 无论请求成功或失败，都执行以下操作
        // 清除本地存储的管理员信息和认证令牌
        adminStore.logout()
        
        // 显示成功提示消息
        Snackbar.success('已退出登录')
        
        // 跳转到登录页面
        // 使用路由名称进行导航，更加语义化
        router.push({ name: 'admin-login' })
      }
    }
  })
}
</script>

<style scoped>
/**
 * 样式说明
 * scoped: 样式作用域限制，确保样式只作用于当前组件，不会影响其他组件
 */

/**
 * 顶部导航栏容器样式
 * 固定高度，水平布局，左右分布
 */
.admin-header {
  display: flex;                    /* 使用 flex 布局 */
  align-items: center;              /* 垂直居中对齐 */
  justify-content: space-between;   /* 左右两端对齐，中间自动分配空间 */
  height: 64px;                     /* 固定高度 64px，与侧边栏顶部对齐 */
  padding: 0 24px;                  /* 左右内边距 24px */
  border-bottom: 1px solid var(--border-color);  /* 底部边框，使用 CSS 变量 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);     /* 阴影效果，增强层次感 */
  background: var(--bg-color);      /* 背景色，使用 CSS 变量便于主题切换 */
  position: relative;               /* 相对定位，为子元素定位提供参考 */
  z-index: 1001;                    /* 层级较高，确保在内容区域之上 */
  backdrop-filter: blur(10px);      /* 背景模糊效果（玻璃态效果），10px 模糊半径 */
}

/**
 * 左侧区域样式
 * 包含菜单图标和页面标题
 */
.header-left {
  display: flex;                    /* 使用 flex 布局 */
  align-items: center;              /* 垂直居中对齐 */
  gap: 16px;                        /* 子元素之间的间距 16px */
}

/**
 * 菜单图标样式
 * 点击可切换侧边栏
 */
.menu-icon {
  cursor: pointer;                   /* 鼠标悬停时显示手型光标 */
  font-size: 24px;                  /* 图标大小 24px */
  color: var(--text-primary);       /* 图标颜色，使用 CSS 变量 */
  transition: color 0.3s;           /* 颜色过渡动画，持续 0.3 秒 */
}

/**
 * 菜单图标悬停状态
 * 鼠标悬停时改变颜色，提供视觉反馈
 */
.menu-icon:hover {
  color: var(--primary-color);      /* 悬停时使用主题色 */
}

/**
 * 页面标题样式
 * 显示当前页面的标题
 */
.header-title {
  font-size: 20px;                  /* 字体大小 20px */
  font-weight: 600;                 /* 字体粗细 600（半粗体） */
  color: var(--text-primary);       /* 文字颜色，使用 CSS 变量 */
  margin: 0;                        /* 移除默认外边距 */
}

/**
 * 右侧区域样式
 * 包含通知图标和管理员信息
 */
.header-right {
  display: flex;                    /* 使用 flex 布局 */
  align-items: center;              /* 垂直居中对齐 */
}

/**
 * 管理员名称样式
 * 显示在头像旁边的管理员姓名
 */
.admin-name {
  font-size: 14px;
  color: var(--text-primary);
}

/* 管理员区域：整块可点，打开菜单 */
.admin-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 20px;
  cursor: pointer;
  color: var(--text-primary);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.admin-trigger:hover {
  background: rgba(0, 0, 0, 0.06);
}
.admin-trigger .arrow-open {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

/* 弹出菜单遮罩：全屏透明，点击关闭 */
.admin-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
}
/* 菜单面板：定位在触发器下方 */
.admin-menu-panel {
  position: fixed;
  z-index: 2001;
  background: var(--bg-color);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
  padding: 4px 0;
}
.admin-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 14px;
  white-space: nowrap;
}
.admin-menu-item:hover {
  background: rgba(0, 0, 0, 0.06);
}
.admin-menu-item:active {
  background: rgba(0, 0, 0, 0.08);
}

/**
 * 响应式设计 - 移动端适配
 * 屏幕宽度 <= 768px 时应用以下样式
 */
@media (max-width: 768px) {
  /* 移动端：减少左右内边距，节省屏幕空间 */
  .admin-header {
    padding: 0 16px;                /* 内边距从 24px 减少到 16px */
  }

  /* 移动端：减小标题字体，适配小屏幕 */
  .header-title {
    font-size: 18px;                /* 字体大小从 20px 减少到 18px */
  }

  /* 移动端：隐藏管理员名称，只显示头像和下拉箭头 */
  /* 节省空间，提升移动端用户体验 */
  .admin-name {
    display: none;                  /* 完全隐藏 */
  }
}
</style>
